import React from 'react';
import { transparentize } from 'polished';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';

import { withSpacingStyles } from '../withSpacingStyles';
import { useComponentTheme } from '../useComponentTheme';
import { Icon, RestrictionIcon } from '../icons';

type Props = {
  title: string,
  icon?: string,
  selected: boolean,
  onClick: Function,
  onRemove: Function,
};

const _Chip = ({ title, icon, selected, onClick, onRemove }: Props) => {
  const theme = useTheme();
  const componentTheme = useComponentTheme(theme => theme.components.Chip, 'default');
  return (
    <ChipWrapper selected={selected} componentTheme={componentTheme} onClick={selected ? onRemove : onClick}>
      {icon && (
        <IconWrapper aria-hidden>
          <RestrictionIconWrapper name={title} iconName={icon} />
        </IconWrapper>
      )}
      <ChipTitle aria-hidden>{title}</ChipTitle>
      <RemoveIcon aria-hidden name="Times" size={12} onClick={onRemove} fill={theme.color.grey} selected={selected} />
      <HiddenInput aria-label={title} checked={selected} name={title} onChange={() => {}} type="checkbox" />
    </ChipWrapper>
  );
};

export const Chip = withSpacingStyles(_Chip);

const ChipWrapper = styled('li')`
  position: relative;
  list-style: none;
  display: flex;
  align-items: center;
  white-space: nowrap;
  min-height: 40px;
  padding: 0 12px;
  border: ${({ componentTheme }) => `1px solid ${componentTheme.borderColor}`};
  color: ${({ componentTheme }) => componentTheme.color};
  border-radius: ${({ componentTheme }) => componentTheme.borderRadius};
  cursor: pointer;

  ${({ selected, theme }) =>
    selected &&
    `
      border: 2px solid ${theme.color.grey};
      background-color: ${transparentize(0.85, theme.color.grey)};
      font-weight: ${theme.fontWeight.bold};
  
  `}
`;

const IconWrapper = styled('div')`
  width: 24px;
  height: 24px;
  display: flex;
  justify-items: center;
  align-items: center;
  margin-right: 8px;
`;

const RestrictionIconWrapper = styled(RestrictionIcon)`
  height: auto;
  max-height: 100%;
  overflow: hidden;
`;

const ChipTitle = styled('div')`
  flex-grow: 1;
  white-space: pre-wrap;
  font-size: ${({ theme }) => theme.fontSize.sm};

  @media (${({ theme }) => theme.breakpoint.desktop}) {
    font-size: ${({ theme }) => theme.fontSize.md};
  }
`;

const RemoveIcon = styled(Icon)`
  visibility: hidden;
  pointer-events: none;
  ${({ selected }) =>
    selected &&
    `
      visibility: visible; 
      pointer-events: all;
  `}
`;

const HiddenInput = styled('input')`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
`;
